import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useRef, useState, type MouseEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import type { TripListRow } from './tripListTypes'

interface Props {
  isBooked: boolean
  row?: TripListRow
  disabled?: boolean
  onBookingChange?: (row: TripListRow, isBooked: boolean) => void
}

function TripListBookedLabel({ isBooked, row, disabled, onBookingChange }: Props) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const editable = Boolean(row && !row.isLuxuryEscapesBooking && onBookingChange)
  const close = useCallback(() => setOpen(false), [])
  const stopPropagation = useCallback((event: MouseEvent) => event.stopPropagation(), [])
  const markBooked = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    if (!row || disabled) return
    if (isBooked) setOpen(value => !value)
    else onBookingChange?.(row, true)
  }, [disabled, isBooked, onBookingChange, row])
  const unmark = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    close()
    if (row && !disabled) onBookingChange?.(row, false)
  }, [close, disabled, onBookingChange, row])
  const bookedLabel = (
    <MessagingLabel variant="success" endIcon={editable ? <LineAngleDownIcon size="XS" aria-hidden /> : undefined}>
      <FormattedMessage id="dsjKlUcoW3tb6U9N7vAr" defaultMessage="Booked" description="Status label for trip planner list rows that are already booked" />
    </MessagingLabel>
  )
  if (!editable) return isBooked ? bookedLabel : null
  const label = isBooked
    ? intl.formatMessage({ defaultMessage: 'Change booking status for {title}', id: 'dPMSf26uDTilfro8ftQ7', description: 'Accessible label for changing a manually marked booking in the itinerary list' }, { title: row?.title })
    : intl.formatMessage({ defaultMessage: 'Mark {title} as booked', id: 'WCtnZsx0DJOojPekRb46', description: 'Accessible label for marking an itinerary item as already booked manually' }, { title: row?.title })
  return (
    <>
      <TextLink
        ref={triggerRef}
        size="caption"
        weight="regular"
        colour="highlight-primary"
        underline={!isBooked}
        onClick={markBooked}
        onMouseDown={stopPropagation}
        disabled={disabled}
        aria-label={label}
        aria-expanded={isBooked ? open : undefined}
      >
        {isBooked && bookedLabel}
        {!isBooked && <FormattedMessage defaultMessage="Mark booked" id="h-HSty1P-qnYgizfInYg" description="Subtle action to record that the traveller has already booked an itinerary item" />}
      </TextLink>
      <ResponsiveDropdownList size="S" open={open && !disabled} onClose={close} triggerRef={triggerRef} anchorRef={triggerRef} placement="bottom-start" enableFlip portaled>
        <Sheet padding={12}>
          <Group direction="vertical" gap={8}>
            <TextButton kind="secondary" size="small" fit="full-width" onClick={unmark} onMouseDown={stopPropagation} disabled={disabled}>
              <FormattedMessage defaultMessage="Mark as not booked" id="2ZSo6yrs7mpsYLfbTC7D" description="Action to clear a manual booked marker from an itinerary item without cancelling it" />
            </TextButton>
            <Caption variant="medium">
              <FormattedMessage defaultMessage="This won't cancel a reservation" id="JJDPfLjpArkrOGXx0mk2" description="Reassurance that clearing a manual booked marker does not cancel a reservation" />
            </Caption>
          </Group>
        </Sheet>
      </ResponsiveDropdownList>
    </>
  )
}

export default TripListBookedLabel
